import React, { useEffect, useMemo } from 'react'
import { v4 as uuid } from 'uuid'
import {
  BehaviorObject,
  clearGroupBehavior,
  ComparisonOperator,
  LayoutColumnType,
  LayoutGroupsType,
  LayoutGroupType,
  LayoutPageType,
  LayoutRowType,
  MacroValuePair,
  setGroupBehavior,
} from '@app-store'
import { fetchMacrosApi } from '../apis/contextual.api'
import { useAppDispatch } from '../utils/agent.store'

function buildGroupToKeyNamesMap(
  pages: LayoutPageType[]
): Record<string, string[]> {
  const map: Record<string, string[]> = {}

  for (const page of pages) {
    for (const row of page.rows as LayoutRowType[]) {
      for (const column of (row.columns ?? []) as LayoutColumnType[]) {
        for (const element of column.elements) {
          const { groupId, keyName } = element
          if (!keyName || !groupId?.length) continue
          for (const gId of groupId) {
            if (!map[gId]) map[gId] = []
            map[gId].push(keyName)
          }
        }
      }
    }
  }

  return map
}

function resolvePath(obj: unknown, parts: string[]): unknown {
  if (!parts.length) return obj
  if (obj === null || obj === undefined) return undefined

  if (Array.isArray(obj)) {
    for (const item of obj) {
      const result = resolvePath(item, parts)
      if (result !== undefined && result !== null) return result
    }
    return undefined
  }

  if (typeof obj !== 'object') return undefined

  const [head, ...rest] = parts
  return resolvePath((obj as Record<string, unknown>)[head], rest)
}

function resolveMacroValue(
  macro: string,
  records: Record<string, unknown>[]
): unknown {
  if (!records?.length) return undefined
  const pathParts = macro.split('.').slice(1)
  for (const record of records) {
    const value = resolvePath(record, pathParts)
    if (value !== undefined && value !== null) return value
  }
  return undefined
}

function evaluateCondition(
  { comparison, macroValue }: MacroValuePair,
  resolvedValue: unknown
): boolean {
  const strValue = String(resolvedValue ?? '').toLowerCase()
  const normalizedMacroValue = macroValue.toLowerCase()
  const opMap: Record<ComparisonOperator, () => boolean> = {
    equals: () => strValue === normalizedMacroValue,
    includes: () => strValue.includes(normalizedMacroValue),
  }
  return opMap[comparison]?.() ?? false
}

function evaluateGroup(
  group: LayoutGroupType,
  macroValues: Record<string, unknown>
): boolean {
  const results = group.conditions.map((cond) =>
    evaluateCondition(cond, macroValues[cond.macro])
  )
  return group.operator?.toUpperCase() === 'AND'
    ? results.every(Boolean)
    : results.some(Boolean)
}

export interface GroupBehaviorProviderProps {
  children: React.ReactNode
  groups: LayoutGroupsType | undefined | null
  pages: LayoutPageType[]
  entityName: string
  recordId: number | string | undefined | null
}

export const GroupBehaviorProvider: React.FC<GroupBehaviorProviderProps> = ({
  children,
  groups,
  pages,
  entityName,
  recordId,
}) => {
  const dispatch = useAppDispatch()
  const providerKey = useMemo(() => uuid(), [])

  const groupList = useMemo(
    () => (groups ? Object.values(groups) : []),
    [groups]
  )

  const groupToKeyNames = useMemo(() => buildGroupToKeyNamesMap(pages), [pages])

  useEffect(() => {
    if (!groupList.length || !recordId || !entityName) {
      dispatch(clearGroupBehavior({ key: providerKey }))
      return
    }

    // Only process groups that have at least one field associated in the layout.
    // This prevents fetching macros for groups with corrupt/unused macro paths
    // that would otherwise cause a 409 error.
    const activeGroupList = groupList.filter(
      (g) => (groupToKeyNames[g.groupId]?.length ?? 0) > 0
    )

    if (!activeGroupList.length) {
      dispatch(clearGroupBehavior({ key: providerKey }))
      return
    }

    const uniqueMacros = Array.from(
      new Set(activeGroupList.flatMap((g) => g.conditions.map((c) => c.macro)))
    )

    let cancelled = false

    const fetchAndEvaluate = async () => {
      try {
        const response = await fetchMacrosApi(
          entityName,
          Number(recordId),
          uniqueMacros
        )
        if (cancelled) return

        const records: Record<string, unknown>[] = response?.data ?? []

        const macroValues: Record<string, unknown> = {}
        for (const macro of uniqueMacros) {
          macroValues[macro] = resolveMacroValue(macro, records)
        }

        const result: BehaviorObject = {}
        for (const group of activeGroupList) {
          if (evaluateGroup(group, macroValues)) {
            const keyNames = groupToKeyNames[group.groupId] ?? []
            result[group.behavior] = [
              ...(result[group.behavior] ?? []),
              ...keyNames,
            ]
          }
        }

        dispatch(setGroupBehavior({ key: providerKey, behaviorObj: result }))
      } catch {
        if (!cancelled) dispatch(clearGroupBehavior({ key: providerKey }))
      }
    }

    fetchAndEvaluate()
    return () => {
      cancelled = true
      dispatch(clearGroupBehavior({ key: providerKey }))
    }
  }, [groupList, groupToKeyNames, entityName, recordId, dispatch, providerKey])

  return <>{children}</>
}
